/* eslint-disable @typescript-eslint/no-floating-promises */
import { useParams, useSearchParams } from 'react-router-dom'
import { compact, isEmpty } from 'lodash'
import moment from 'moment'
import { useEffect, useMemo, useRef, useState } from 'react'
import { debug } from 'lib/logging'
import TrackableLongLogo from 'app/components/Icon/TrackableLongLogo'
import { AppointmentConfig } from 'app/components/scheduling/AppointmentConfig'
import { AppointmentConfirmation } from 'app/components/scheduling/AppointmentConfirmation'
import { AppointmentForm } from 'app/components/scheduling/AppointmentForm'
import { AppointmentTypeSelector } from 'app/components/scheduling/AppointmentType'
import { OverrideConfirmation } from 'app/components/scheduling/OverrideConfirmation'
import { OverrideForm } from 'app/components/scheduling/OverrideForm'
import { PatientStatus } from 'app/components/scheduling/PatientStatus'
import SchedulingWizard from 'app/components/scheduling/SchedulingWizard'
import Spinner from 'app/components/Spinner'
import { useImmediateRequest } from 'app/hooks/useImmediateRequest'
import { AppointmentTypeType } from 'app/types/entities'
import { AvailabilityType } from 'app/types/entities'
import { ProviderType } from 'app/types/entities'
import { DaySlotType, PatientStatusType, SchedulingDataApiResponse } from 'lib/scheduling'
import { buildPreviewUrl } from 'lib/schedulingPreviewUrl'

function SchedulingErrorPage() {
  return (
    <div className="theme-light bg-surface text-strong flex h-full flex-col items-center justify-center gap-4 p-6 text-center">
      <div className="text-lg font-medium text-default">Online scheduling is temporarily unavailable</div>
      <div className="text-sm text-subtle">Please try again later or call the office to schedule your appointment.</div>
      <button
        onClick={() => window.location.reload()}
        className="mt-4 rounded-lg bg-trn-600 px-6 py-2 text-sm font-medium text-on-color hover:bg-trn-700"
      >
        Try Again
      </button>
    </div>
  )
}

export default function SchedulingPage() {
  const { id } = useParams<{ id: string }>()
  const [searchParams] = useSearchParams()
  const isPreview = searchParams.get(`preview`) === `true`
  const withPreview = (url: string) => buildPreviewUrl(url, isPreview)
  const [location, setLocation] = useState<any>()
  const [locationError, setLocationError] = useState(false)
  useEffect(() => {
    if (!id) return
    fetch(withPreview(`/api/scheduling/locations/${id}/info`))
      .then((r) => r.json())
      .then((d) => {
        if (d && !d.error) setLocation(d)
        else setLocationError(true)
      })
      .catch(() => setLocationError(true))
  }, [id])
  const [imgLoadFailed, setImgLoadFailed] = useState<boolean | undefined>()
  const imgRef = useRef<HTMLImageElement>(null)

  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth === 0) setImgLoadFailed(true)
  }, [])

  // Client-side data fetching
  // Appointment types load from a fast, public endpoint (active types only, no login).
  // This lets the type selector render at once, without waiting for the heavy
  // scheduling-data payload (PMS + slots).
  const apptTypesUrl = location?.id
    ? withPreview(`/api/scheduling/locations/${location.id}/public-appointment-types`)
    : ``
  const [apptTypesResponse, apptTypesError] = useImmediateRequest<{
    appointmentTypes: AppointmentTypeType[]
    pmsStatus?: `ok` | `error`
  }>(apptTypesUrl, false)
  const appointmentTypes = apptTypesResponse?.appointmentTypes

  // undefined = initial load, string = user navigated months
  const [day, setDay] = useState<string>()
  const initialDataUrl = location?.id ? withPreview(`/api/scheduling/locations/${location.id}/scheduling-data`) : ``
  const dataUrl = day
    ? withPreview(`/api/scheduling/locations/${location.id}/scheduling-data?day=${day}`)
    : initialDataUrl
  const [dataResponse, dataError] = useImmediateRequest<SchedulingDataApiResponse>(dataUrl, false)

  // Stale-while-revalidate: keep previous data visible during month transitions
  const [lastData, setLastData] = useState<SchedulingDataApiResponse>()
  useEffect(() => {
    if (dataResponse) {
      setLastData(dataResponse)
    }
  }, [dataResponse])

  const data = lastData || dataResponse
  const providers = data?.providers
  const availabilities = data?.availabilities
  const slots = data?.slots
  // The logo loads from a fast, public endpoint used directly in the img tag. The image
  // shows at once, without the heavy scheduling-data payload. The img onError handler
  // hides the logo when the location has none.
  const logoUrl = location?.id ? `/api/locations/${location.id}/scheduling/logo` : undefined
  // CareStack and Ascend both serve availability through month-availability / find-slots
  // (the API's month-availability endpoint has a dedicated Ascend branch). Gating this flow
  // on carestackId alone left Ascend locations with no availability past the initial
  // scheduling-data days. See TRK-6165.
  const isExternalSlots = !!location?.carestackId || !!location?.ascendLocationId

  // The month the calendar shows is only set when the patient changes it. Until then the
  // current month applies. It cannot be the useState initial value: the first render runs
  // before /info answers, so isExternalSlots is still false there and the month would stay
  // unset, which leaves the calendar on "Loading availability..." for good.
  const [selectedMonth, setSelectedMonth] = useState<string>()
  const csMonth = selectedMonth ?? moment().format(`YYYY-MM`)
  const [selectedCsApptTypeId, setSelectedCsApptTypeId] = useState<string | undefined>(undefined)
  const [selectedProductionTypeId, setSelectedProductionTypeId] = useState<string | undefined>(undefined)
  // Ascend availability can differ by patient type; the API forwards it to Ascend only.
  const [selectedPatientType, setSelectedPatientType] = useState<PatientStatusType | undefined>(undefined)

  const monthAvailabilityUrl =
    isExternalSlots && selectedProductionTypeId
      ? withPreview(
          `/api/scheduling/locations/${
            location!.id
          }/month-availability?month=${csMonth}&productionTypeId=${selectedProductionTypeId}${
            selectedPatientType ? `&patientType=${encodeURIComponent(selectedPatientType)}` : ``
          }`,
        )
      : ``

  const [monthAvailabilityResponse] = useImmediateRequest<{
    dates: string[]
    slots?: DaySlotType[]
    month: string
    productionTypeId: string
  }>(monthAvailabilityUrl)

  const [findSlotDay, setFindSlotDay] = useState<string>()
  const findSlotUrl =
    findSlotDay && isExternalSlots
      ? withPreview(
          `/api/scheduling/locations/${location!.id}/find-slots?day=${findSlotDay}${selectedCsApptTypeId ? `&appointmentTypeId=${selectedCsApptTypeId}` : ``}`,
        )
      : ``
  const [findSlotResponse] = useImmediateRequest<{ slots: DaySlotType[]; isExternalSlots: boolean }>(findSlotUrl, false)
  // month-availability returns the time slots of every available date in the month, so the
  // wizard can preselect the first one without a click. find-slots comes last: it answers
  // for the one day the patient picks, so its slots replace that day's month slots.
  const carestackSlots = useMemo(
    () => [...(monthAvailabilityResponse?.slots ?? []), ...(findSlotResponse?.slots ?? [])],
    [monthAvailabilityResponse, findSlotResponse],
  )
  const addressLine1 = location?.address
  const addressLine2 = location ? compact([location.city, location.state, location.zip]).join(`, `) : ``
  // eslint-disable-next-line no-console
  if (data?.timing) debug(`scheduling`, `timing`, data.timing)

  const isDataLoading = !dataResponse

  const renderCorrectFlow = (
    regularFlow: React.ReactNode[],
    overrideFlow: React.ReactNode[],
    patientType?: PatientStatusType,
    slots?: DaySlotType[],
  ) => {
    // Read the fast type-picker signal, so the flow decides before the calendar renders.
    if (apptTypesResponse?.pmsStatus === `error`) return overrideFlow
    if (patientType === `Existing Patient`) return regularFlow
    if (!data) return regularFlow

    if (
      data.schedulingConfiguration &&
      data.schedulingConfiguration.leadOverrideEnabled &&
      data.schedulingConfiguration.leadOverrideConfig &&
      // Override check only applies on initial load (day not yet set by user)
      day === undefined &&
      !slots?.some((slot, i) =>
        i <=
        data.schedulingConfiguration!.leadOverrideConfig!.amount *
          (data.schedulingConfiguration!.leadOverrideConfig!.unit === `weeks` ? 7 : 1) -
          1
          ? slot.timeSlots.some((timeSlot) => timeSlot.availabilities.length)
          : false,
      )
    )
      return overrideFlow

    return regularFlow
  }
  if (!location) {
    return (
      <div className="theme-light text-strong h-dvh flex flex-col bg-surface md:overflow-y-auto md:bg-surface-muted">
        <div className="flex h-full flex-col items-center md:h-fit md:shrink-0 md:grow md:pt-6">
          <div className="flex h-full w-full grow flex-col items-center justify-center rounded-xl border-subtle bg-surface shadow-elevation-4 md:m-auto md:h-fit md:min-h-[780px] md:w-[720px] md:grow-0">
            {locationError ? <SchedulingErrorPage /> : <Spinner size={16} />}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="theme-light text-strong h-dvh flex flex-col bg-surface md:overflow-y-auto md:bg-surface-muted">
      <style
        dangerouslySetInnerHTML={{
          __html: `.react-calendar__month-view__days__day--neighboringMonth { display: block; visibility: hidden }`,
        }}
      ></style>
      <div className="flex h-full flex-col items-center md:h-fit md:shrink-0 md:grow md:pt-6">
        <div className="flex h-full w-full grow flex-col items-center justify-center rounded-xl border-subtle bg-surface shadow-elevation-4 md:m-auto md:h-fit md:min-h-[780px] md:w-[720px] md:grow-0">
          {isPreview && (
            <div className="w-full rounded-t-xl border-b border-default bg-blue-50 px-6 py-3 text-sm text-blue-800">
              Welcome! Let us know if you find any discrepancies in the time slots offered. Sharing dates that are
              further out gives us time to investigate.
            </div>
          )}
          <div
            className="flex
          w-full justify-between border-b border-default px-6 py-4"
          >
            <div className="flex h-20 w-[300px] items-start bg-transparent">
              {logoUrl && !imgLoadFailed ? (
                <img
                  ref={imgRef}
                  src={logoUrl}
                  height={80}
                  width={300}
                  className="h-20 w-auto object-contain"
                  alt={location?.name || ``}
                  onError={() => setImgLoadFailed(true)}
                  onLoad={(e) => {
                    if ((e.target as HTMLImageElement).naturalWidth === 0) setImgLoadFailed(true)
                  }}
                />
              ) : (
                location?.name && <span className="text-ui-lg">{location.name}</span>
              )}
            </div>
            <div className="flex shrink-0 flex-col text-right text-xs font-medium text-default">
              {addressLine1 && <span>{addressLine1}</span>}
              {addressLine2 && <span>{addressLine2}</span>}
            </div>
          </div>
          <SchedulingWizard
            appointmentTypes={(appointmentTypes || []) as AppointmentTypeType[]}
            allProviders={providers as ProviderType[]}
            allAvailabilities={availabilities as AvailabilityType[]}
            preProcessedSlots={(slots as DaySlotType[]) || []}
            location={location}
            lookaheadDays={data?.lookaheadDays}
            startDate={data?.day || moment().format(`YYYY-MM-DD`)}
            schedulingConfiguration={
              data?.schedulingConfiguration || {
                granularity: 30,
                allowDoubleBooking: false,
                leadOverrideEnabled: false,
              }
            }
            isDataLoading={isDataLoading}
            isPreview={isPreview}
            onMonthChange={setDay}
            resetDay={() => setDay(undefined)}
            isExternalSlots={isExternalSlots}
            onDayClick={isExternalSlots ? setFindSlotDay : undefined}
            findSlotData={carestackSlots}
            carestackAvailableDates={monthAvailabilityResponse?.dates}
            onCsMonthChange={isExternalSlots ? setSelectedMonth : undefined}
            onCsAppointmentTypeSelected={
              isExternalSlots
                ? (vendorId: string | undefined, apptTypeId?: string) => {
                    setSelectedProductionTypeId(vendorId)
                    setSelectedCsApptTypeId(apptTypeId)
                  }
                : undefined
            }
            onCsPatientTypeChange={isExternalSlots ? setSelectedPatientType : undefined}
          >
            {(patientType, slots) => (
              <>
                <SchedulingWizard.Steps>
                  <PatientStatus key="step-0" />
                  {apptTypesError ? <SchedulingErrorPage key="step-1" /> : <AppointmentTypeSelector key="step-1" />}
                  {renderCorrectFlow(
                    [
                      dataError ? (
                        <SchedulingErrorPage key="step-2" />
                      ) : (
                        <AppointmentConfig key="step-2" location={location!} startDate={data?.day} />
                      ),
                      <AppointmentForm
                        key="step-3"
                        location={location!}
                        schedulingConfiguration={data?.schedulingConfiguration!}
                        startDate={data?.day}
                      />,
                      <AppointmentConfirmation key="step-4" startDate={data?.day} />,
                    ],
                    [
                      dataError ? (
                        <SchedulingErrorPage key="step-2" />
                      ) : (
                        <OverrideForm
                          key="step-2"
                          location={location!}
                          schedulingConfiguration={data?.schedulingConfiguration!}
                        />
                      ),
                      <OverrideConfirmation key="step-3" />,
                    ],
                    patientType,
                    slots,
                  )}
                </SchedulingWizard.Steps>
                {<SchedulingWizard.Buttons schedulingConfiguration={data?.schedulingConfiguration!} />}
              </>
            )}
          </SchedulingWizard>
        </div>
      </div>
      {!isEmpty(location) && (
        <div className="hidden shrink items-center justify-center gap-x-1 pb-4 md:flex md:pt-2">
          <span className="mr-1 text-body-xs text-minimal">Powered by</span>
          <TrackableLongLogo className="h-5 w-20 shrink-0 rounded-md bg-surface-muted text-default" />
        </div>
      )}
    </div>
  )
}
